import { FC, useCallback, useState } from "react";

import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { IconText } from "mds/components/IconText";
import { ArrowDownIcon, LoadingDotsIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import {
  selectAllVisibleClassSessionsForCurrentSection,
  selectCurrentSectionTitle,
  selectOrgUserByUserIdMap,
  selectStudentClassSessionUserSummaries,
  selectUsersInCurrentSection,
} from "store/selectors";
import { useListSelector } from "store/store-hooks";
import { toastErrorMessage } from "utils/alerts";
import { downloadCSVFile } from "utils/file";
import { generateAttendanceReportCSVFromSessions } from "utils/reporting";

export const AttendanceReportButton: FC = () => {
  const sectionTitle = useAppSelector(selectCurrentSectionTitle);
  const allVisibleClassSessions = useAppSelector(selectAllVisibleClassSessionsForCurrentSection);
  const studentClassSessionUserSummaries = useAppSelector(selectStudentClassSessionUserSummaries);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const sectionUsers = useListSelector(selectUsersInCurrentSection);
  const orgUsersMap = useAppSelector(selectOrgUserByUserIdMap);

  const handleDownloadAttendanceReport = useCallback(() => {
    if (!allVisibleClassSessions || allVisibleClassSessions.length === 0) {
      toastErrorMessage(t("session_log_view.export.no_data"));
      return;
    }

    if (!sectionTitle) {
      toastErrorMessage(t("session_log_view.export.failed"));
      return;
    }

    setIsGeneratingReport(true);
    try {
      const { csvData, filename } = generateAttendanceReportCSVFromSessions(
        allVisibleClassSessions,
        sectionUsers,
        orgUsersMap,
        sectionTitle,
        studentClassSessionUserSummaries,
      );
      downloadCSVFile(csvData, filename);
    } catch {
      toastErrorMessage(t("session_log_view.export.failed"));
    } finally {
      setIsGeneratingReport(false);
    }
  }, [
    allVisibleClassSessions,
    sectionUsers,
    sectionTitle,
    orgUsersMap,
    studentClassSessionUserSummaries,
  ]);

  return (
    <Button
      disabled={
        !allVisibleClassSessions.length ||
        !sectionUsers.length ||
        !sectionTitle ||
        !studentClassSessionUserSummaries.length ||
        isGeneratingReport
      }
      kind="white"
      size="s"
      onClick={handleDownloadAttendanceReport}
    >
      <IconText
        iconStart={isGeneratingReport ? <LoadingDotsIcon /> : <ArrowDownIcon />}
        text={t("session_log_view.attendance_report.download_button")}
      />
    </Button>
  );
};
